'use client';

import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { FiPlus } from 'react-icons/fi';

import { Paper, Stack, UnstyledButton } from '@highlight-cp/core-ui';

import classes from './add-row-button.module.css';

interface AddRowButtonBaseProps {
  dataTestId?: string;
  disabled?: boolean;
  /** Omit to leave the button inert — for a flow whose add step hasn't landed yet. */
  onClick?: () => void;
  /** The rows appended so far, framed with the button. Omit to leave the list outside the frame. */
  rows?: ReactNode;
  /** Set `false` to drop the grey frame, leaving the dashed button alone. @default true */
  withFrame?: boolean;
}

/**
 * Either the button carries its own text (`label`) or it is the bare `+`, which needs an
 * `ariaLabel` to be named at all — so one of the two is always required.
 */
export type AddRowButtonProps = AddRowButtonBaseProps &
  (
    | {
        /** Names the button for assistive tech — the row it appends, e.g. "Add product". */
        ariaLabel: string;
        label?: string;
      }
    | {
        ariaLabel?: string;
        /** Text in place of the `+` glyph, e.g. "+ New product". Names the button by itself. */
        label: string;
      }
  );

/**
 * ### AddRowButton
 *
 * @description
 * A dashed, full-width button that appends an empty row to the list it sits under. The affordance
 * the audience step's editable lists share — products, and the criteria sections after them — in
 * place of the side panel a row used to be dragged in from, and the one the project builder's
 * sidebar and products library use to add a product or an audience.
 *
 * Pass `rows` and the frame holds the list too, the button beneath it; leave it off and the frame
 * holds only the button, with the rows above it in the caller's own layout. Either way the
 * forwarded ref is the button itself, not the frame — it's there so a caller can return focus
 * after removing a row.
 *
 * By default it is a `+` on a padded grey frame, with `ariaLabel` as its only name — the rows it
 * appends are already headed, and the design leaves the glyph to speak for itself. Pass `label` for
 * the text form, and `withFrame={false}` where the surrounding surface already frames it.
 *
 * @example
 * ```tsx
 * <AddRowButton ariaLabel={t('Add product')} onClick={appendRow} disabled={isReadOnly} />
 * <AddRowButton ariaLabel={t('Add criteria')} onClick={appendRow} rows={criteriaRows} />
 * <AddRowButton label={t('+ New product')} onClick={appendRow} withFrame={false} />
 * ```
 *
 * @param props - {@link AddRowButtonProps}
 * @returns React component
 */
export const AddRowButton = forwardRef<HTMLButtonElement, AddRowButtonProps>(
  ({ ariaLabel, dataTestId, disabled = false, label, onClick, rows, withFrame = true }, ref) => {
    // A single child spaces the same as none, so the frame is unchanged without `rows`.
    const content = (
      <Stack gap="xs">
        {rows}
        <UnstyledButton
          aria-label={ariaLabel}
          className={classes.root}
          data-testid={dataTestId}
          disabled={disabled}
          mod={{ labelled: Boolean(label) }}
          onClick={onClick}
          ref={ref}
          type="button"
        >
          {label ?? <FiPlus aria-hidden size={20} />}
        </UnstyledButton>
      </Stack>
    );

    if (!withFrame) return content;

    // No border: the fill alone marks the frame out against the card, and an edge around it read
    // as a second card nested in the one it sits on.
    return (
      <Paper bg="var(--mantine-color-gray-light)" p="xs" radius="md">
        {content}
      </Paper>
    );
  },
);

AddRowButton.displayName = 'AddRowButton';
